import { Component, inject, signal, computed, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { BusinessContextService, BusinessContext } from '../../core/services/business-context.service';
import { LoadingService } from '../../core/services/loading.service';

@Component({
    selector: 'app-select-business',
    standalone: true,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    template: `
    <div class="w-full h-full animate-fade-in-up">
        <div class="relative py-8 md:py-4">

            <!-- Page Header -->
            <div class="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <div class="space-y-4">
                    <div class="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-foreground/85">
                        <span class="text-primary">Sesión Activa</span>
                    </div>
                    <h1 class="text-3xl font-bold text-slate-800 dark:text-white/90 tracking-tighter uppercase leading-none">
                        Seleccionar Negocio
                    </h1>
                    <p class="text-xs text-foreground font-medium max-w-md">
                        Hola, <strong class="text-primary">{{ userName() }}</strong>.
                        Elige el espacio de trabajo con el que deseas continuar.
                    </p>
                </div>
            </div>

            <!-- Error -->
            <div *ngIf="error()"
                class="p-6 bg-red-500/5 border border-red-500/15 rounded-2xl mb-8">
                <p class="text-sm text-red-500 font-bold">{{ error() }}</p>
                <button (click)="loadBusinesses()"
                    class="mt-3 text-xs font-black text-primary underline">Reintentar</button>
            </div>

            <!-- Business Cards Grid -->
            <div *ngIf="businesses().length > 0"
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

                <button *ngFor="let b of businesses(); let i = index"
                    (click)="selectBusiness(b)"
                    [style.animation-delay]="(i * 60) + 'ms'"
                    class="group text-left p-4 rounded-2xl border border-foreground/[0.1]
                           bg-surface/60 backdrop-blur-md
                           hover:bg-primary/5 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5
                           transition-all duration-500 active:scale-[0.97]
                           animate-fade-in-up relative overflow-hidden">

                    <!-- Decorative gradient -->
                    <div class="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl
                                group-hover:bg-primary/10 transition-all duration-700 -translate-x-4 -translate-y-8">
                    </div>

                    <!-- Icon -->
                    <div class="relative w-12 h-12 rounded-xl bg-primary/10 border border-primary/20
                                flex items-center justify-center mb-4
                                group-hover:bg-primary/15 group-hover:border-primary/30
                                transition-all duration-300">
                        <iconify-icon icon="mdi:office-building-outline" width="20" height="20"
                            class="text-primary group-hover:text-primary transition-colors duration-300">
                        </iconify-icon>
                    </div>

                    <!-- Info -->
                    <div class="relative">
                        <p class="text-[14px] font-bold text-slate-800 dark:text-white/90 tracking-tight
                                  group-hover:text-primary transition-colors duration-300 mb-1 truncate">
                            {{ b.businessName ?? b.name ?? 'Negocio' }}
                        </p>
                        <p class="text-[9px] font-bold text-foreground/90 uppercase tracking-widest mb-3 truncate">
                            {{ b.role?.name ?? b.roleName ?? (isSuperAdmin() ? 'Super Admin' : 'Miembro') }}
                        </p>

                    </div>

                    <!-- Arrow indicator -->
                    <div class="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all
                                duration-300 transform translate-x-2 group-hover:translate-x-0">
                        <iconify-icon icon="mdi:arrow-right" width="14" height="14" class="text-primary">
                        </iconify-icon>
                    </div>
                </button>
            </div>

            <!-- Empty State -->
            <div *ngIf="businesses().length === 0 && !error()"
                class="py-24 text-center">
                <div class="w-16 h-16 rounded-2xl bg-foreground/10 flex items-center justify-center mx-auto mb-6">
                    <iconify-icon icon="mdi:office-building-remove-outline" width="32" height="32"
                        class="text-foreground/70"></iconify-icon>
                </div>
                <p class="text-sm font-bold text-slate-800 dark:text-white/90/90 mb-2 uppercase tracking-wider">Sin negocios asignados</p>
                <p class="text-xs text-foreground/50">Contacta a tu administrador.</p>
            </div>

        </div>
    </div>
    `,
    styles: [`
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(15px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.4s cubic-bezier(0.16,1,0.3,1) both;
        }
    `]
})
export class SelectBusiness implements OnInit {
    private authService = inject(AuthService);
    private businessCtx = inject(BusinessContextService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private loadingService = inject(LoadingService);

    error = signal('');
    businesses = signal<any[]>([]);
    isSuperAdmin = signal(false);

    readonly userName = computed(() =>
        this.authService.currentUserValue?.fullName?.split(' ')?.shift() ?? 'Usuario'
    );

    ngOnInit() {
        this.loadBusinesses();
    }

    loadBusinesses() {
        this.error.set('');
        this.loadingService.showLoading('Sincronizando', 'Buscando tus negocios asignados...');

        this.authService.getMyBusinesses().subscribe({
            next: (response: any) => {
                this.isSuperAdmin.set(response.isSuperAdmin ?? false);
                const list = response.businesses ?? [];

                this.businesses.set(list);
                this.loadingService.hideLoading();

                // Auto-selección solo si viene de flujo de login/inicial (?auto=true)
                const autoParam = this.route.snapshot.queryParams['auto'];
                if (list.length === 1 && autoParam === 'true') {
                    this.selectBusiness(list[0]);
                }
            },
            error: (err: any) => {
                this.error.set('No se pudieron cargar los negocios. ' + (err.message ?? ''));
                this.loadingService.hideLoading();
            }
        });
    }

    selectBusiness(b: any) {
        this.businessCtx.selectFromRaw(b, this.isSuperAdmin());
        this.router.navigate(['/app/dashboard']);
    }

}
